import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IconButton, Paper, Typography, useTheme
} from '@mui/material';
import { Text } from 'components/text';
import { ChargeSetting } from 'graphql/charge-settings/queries';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultForAll, formatPartyLabel, formatPercentLabel, getSettingName
} from 'views/charge-settings/utils';
import { useStyles } from './styles';

type MobileSettingCardProps = {
  setting: ChargeSetting,
  onDelete: (settingId: string) => void,
  onEdit: (setting: ChargeSetting) => void,
};

type LabeledValueProps = {
  label: string,
  value: string | number,
  isMoney?: boolean,
};

const LabeledValue: FC<LabeledValueProps> = ({ label, value, isMoney = false }) => {
  const classes = useStyles();

  return (
    <div className={classes.field}>
      <Typography variant="caption" className={classes.label}>{label}</Typography>
      <Text value={value} isMoney={isMoney} color="textPrimary" />
    </div>
  );
};

export const MobileSettingCard: FC<MobileSettingCardProps> = ({
  setting,
  onDelete,
  onEdit
}) => {
  const {
    payees,
    payers,
    buyer_before,
    buyer_at,
    seller_before,
    seller_at,
    by_others,
    amount_override
  } = setting;
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const settingName = getSettingName(setting);
  const buyerTitleId = `charge-setting-${setting._id}-buyer`;
  const sellerTitleId = `charge-setting-${setting._id}-seller`;

  return (
    <Paper className={classes.card} variant="outlined">
      <div className={classes.header}>
        <div className={classes.title}>
          <Text value={settingName} variant="subtitle2" fontWeight="500" color="textPrimary" />
        </div>
        <div className={classes.actions}>
          <IconButton
            aria-label={`${t('common:edit')} ${settingName}`}
            onClick={() => onEdit(setting)}
            size="large"
          >
            <FontAwesomeIcon icon="edit" size="xs" color={theme.palette.secondary.main} />
          </IconButton>
          {settingName !== defaultForAll && (
            <IconButton
              aria-label={`${t('common:delete')} ${settingName}`}
              onClick={() => onDelete(setting._id)}
              size="large"
            >
              <FontAwesomeIcon icon="trash" size="xs" color={theme.palette.error.main} />
            </IconButton>
          )}
        </div>
      </div>

      <LabeledValue label={t('common:payeesLabel')} value={formatPartyLabel(payees)} />
      <LabeledValue label={t('common:payersLabel')} value={formatPartyLabel(payers)} />

      <div className={classes.groups}>
        <div className={classes.group} role="group" aria-labelledby={buyerTitleId}>
          <Typography id={buyerTitleId} variant="subtitle2" className={classes.groupTitle}>
            {t('common:buyer')}
          </Typography>
          <LabeledValue label={t('common:beforeClosingLabel')} value={formatPercentLabel(buyer_before)} />
          <LabeledValue label={t('common:atClosingLabel')} value={formatPercentLabel(buyer_at)} />
        </div>
        <div className={classes.group} role="group" aria-labelledby={sellerTitleId}>
          <Typography id={sellerTitleId} variant="subtitle2" className={classes.groupTitle}>
            {t('common:seller')}
          </Typography>
          <LabeledValue label={t('common:beforeClosingLabel')} value={formatPercentLabel(seller_before)} />
          <LabeledValue label={t('common:atClosingLabel')} value={formatPercentLabel(seller_at)} />
        </div>
      </div>

      <div className={classes.footer}>
        <LabeledValue label={t('common:byOthersLabel')} value={formatPercentLabel(by_others)} />
        <LabeledValue label={t('common:presetAmountLabel')} value={amount_override} isMoney />
      </div>
    </Paper>
  );
};
